/**
 * Engine version guard — ADR-005 §7: the adapter asserts the engine revision
 * it was validated against and fails at boot with a clear message outside it.
 * v2's silent 0.160-CDN vs ^0.180-package.json drift must be unrepresentable.
 */
import { REVISION } from 'three';

/** The revision range this adapter's conversions are validated against. */
export const VALIDATED_THREE_REVISIONS = Object.freeze({ min: 185, max: 185 });

export class UnsupportedEngineRevisionError extends Error {
  constructor(readonly revision: string) {
    super(
      `three@r${revision} is outside the validated range ` +
        `r${VALIDATED_THREE_REVISIONS.min}–r${VALIDATED_THREE_REVISIONS.max}. ` +
        `Re-validate the adapter fixtures and widen the range deliberately (ADR-005 §7) — ` +
        `do not suppress this check.`,
    );
    this.name = 'UnsupportedEngineRevisionError';
  }
}

/** Parse three's REVISION ("185" or "185dev") to its integer release line. */
export function parseRevision(revision: string): number {
  const parsed = Number.parseInt(revision, 10);
  if (Number.isNaN(parsed)) {
    throw new UnsupportedEngineRevisionError(revision);
  }
  return parsed;
}

/** Call at boot. Throws loudly on an unvalidated engine revision. */
export function assertValidatedEngine(revision: string = REVISION): number {
  const r = parseRevision(revision);
  if (r < VALIDATED_THREE_REVISIONS.min || r > VALIDATED_THREE_REVISIONS.max) {
    throw new UnsupportedEngineRevisionError(revision);
  }
  return r;
}
